import { useCallback } from "react";
import { PageEditSessionService } from "utilities/services/notebooks/page-edit-session-service";
import useCurrentIdentity from "utilities/hooks/use-current-identity";

interface UseReadonlyChecklistEditGuard {
    guardAction: () => Promise<boolean>;
}

/** This hook ensures users cannot edit a checklist block if another user is currently editing it */
const useReadonlyChecklistEditGuard = (
    pageId: number,
    blockId: number | undefined,
    onRequestEditMode?: () => Promise<void>
): UseReadonlyChecklistEditGuard => {
    const { currentIdentity } = useCurrentIdentity();
    const { get: getActiveSession } =
        PageEditSessionService.useGetActiveSessionQuery();

    const { refetch } = getActiveSession(
        undefined,
        { pageId },
        {
            enabled: false,
        }
    );

    const guardAction = useCallback(async (): Promise<boolean> => {
        try {
            const response = await refetch();
            const session = response.data?.resultObject;

            if (!session || session.userId === currentIdentity?.userId()) {
                return true;
            }

            // Only block if the other user is editing this specific block
            if (session.blockId !== blockId) {
                return true;
            }

            await onRequestEditMode?.();
            return false;
        } catch {
            // If we can't check the session, allow the action to proceed
            return true;
        }
    }, [refetch, currentIdentity, onRequestEditMode, blockId]);

    return { guardAction };
};

export default useReadonlyChecklistEditGuard;
